/* eslint-disable @typescript-eslint/no-unused-vars */
import { SearchOutlined } from '@ant-design/icons';
import { Row, Col, DatePicker, Button, message } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, SubmitButton } from 'formik-antd';
import moment, { Moment } from 'moment';
import { getDetailMachine, updateMachine } from 'pages/ListMachine/thunk';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import { RootState } from 'store';
import { HeaderTitle, ModalWarningLeavePage, Rectangle } from 'components';
import { t_equipment_lease, t_machine } from 'types';
import TableUpdate from './TableUpdate';
import Wrap from './styles';
import { filterListPatient, updateDevice, updateBackDevice } from './thunk';

type InitialValues = {
  lease_start_date: string;
  lease_end_date: string;
};

const formatDate = 'YYYY年MM月DD日';
const MachineUpdate: FC = () => {
  const { itemDetail, listPatient } = useSelector((state: RootState) => state.machine);
  const leaseContractIdItem = itemDetail?.lease_contract_id;
  const newStartDate = moment(itemDetail?.lease_start_date).format('YYYY-MM-DDThh:mm:ss.SSSZ');
  const newEndDate = moment(itemDetail?.lease_end_date).format('YYYY-MM-DDThh:mm:ss.SSSZ');
  const [startDate, setStartDate] = useState<string>(newStartDate || '');
  const [endDate, setEndDate] = useState<string>(newEndDate || '');
  const [data, setData] = useState<t_machine[]>(itemDetail?.machines || []);
  const [devicesReturn, setDeviceReturn] = useState<string[]>([]);
  const [devicesBack, setDeviceBack] = useState<string[]>([]);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ itemId: string }>();
  useEffect(() => {
    if (location.state) {
      dispatch(getDetailMachine(location.state.itemId));
    }
  }, [dispatch, location.state]);
  useEffect(() => {
    dispatch(filterListPatient(leaseContractIdItem || ''));
  }, [dispatch, leaseContractIdItem]);
  const onChangeStartDate = (value: any, dateString: string) => {
    setStartDate(dateString);
  };

  const validateStartDate = (dateString: Moment) => {
    return dateString && dateString > moment(endDate).startOf('day');
  };

  const validateEndDate = (dateString: Moment) => {
    return dateString && dateString < moment(startDate).endOf('day');
  };

  const onChangeEndDate = (value: any, dateString: string) => {
    setEndDate(dateString);
  };
  const initialValues: InitialValues = {
    lease_start_date: '',
    lease_end_date: '',
  };
  const handleSubmit = async (values: InitialValues, form: FormikHelpers<InitialValues>) => {
    const dateUpdate: t_equipment_lease = {
      lease_start_date: startDate,
      lease_end_date: endDate,
      i_id: itemDetail?.i_id,
    };
    const resultUpdateEquipment: any = await dispatch(
      updateMachine({ item: dateUpdate, machine: data, return_item_result: true }),
    );
    const resultUpdateDevice: any = await dispatch(updateDevice(devicesReturn));
    const resultUpdateBackDevice: any = await dispatch(updateBackDevice(devicesBack));
    if (
      !resultUpdateEquipment.payload.error &&
      !resultUpdateDevice.payload.has_error &&
      !resultUpdateBackDevice.payload.has_error
    ) {
      history.push('/machine/detail', { itemId: itemDetail?.i_id, omitWarningLeavePage: true });
    } else {
      message.error('システムエラーが発生しました。');
    }
  };

  const changeDeviceReturn = (machineId: string, checked: boolean) => {
    const index = devicesReturn.findIndex((f) => f === machineId);
    const back_index = devicesBack.findIndex((f) => f === machineId);
    if (checked) {
      if (index < 0) {
        setDeviceReturn((oldState) => [...oldState, machineId]);
      }
      if (back_index > -1) {
        const newArr = [...devicesBack];
        newArr.splice(back_index, 1);
        setDeviceBack(newArr);
      }
    } else {
      if (back_index < 0) {
        setDeviceBack((oldState) => [...oldState, machineId]);
      }
      if (index > -1) {
        const newArr = [...devicesReturn];
        newArr.splice(index, 1);
        setDeviceReturn(newArr);
      }
    }
  };

  if (!location.state) {
    return <Redirect to="/machine" />;
  }

  return (
    <Wrap>
      <HeaderTitle title="リース契約詳細" icon={SearchOutlined} />
      <div className="lease-renewal">
        <span className="extraLargeFont">リース更新</span>
      </div>
      <div className="intro-update">
        <span className="standardFont">
          更新後のリース期間を入力し、返却する機材がある場合は「返却」にチェックを入れて更新してください。
        </span>
      </div>
      <div className="section">
        <Rectangle text="リース情報" />
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          render={() => (
            <Form>
              <div className="update-lease">
                <div className="lease-statistics">
                  <Row className="row-statistics mb-30">
                    <Col className="standardBoldFont" span={5}>
                      リース期間
                    </Col>
                    <Col style={{ display: 'flex', alignItems: 'baseline' }} span={19}>
                      <Form.Item name="lease_start_date">
                        <DatePicker
                          onChange={onChangeStartDate}
                          disabledDate={validateStartDate}
                          defaultValue={moment(itemDetail?.lease_start_date)}
                          className="input-date standardFont"
                          name="lease_start_date"
                          size={'large'}
                          locale={{
                            lang: {
                              locale: 'ja',
                              placeholder: 'Select date',
                              rangePlaceholder: ['Start date', 'End date'],
                              today: 'Today1',
                              now: 'Now',
                              backToToday: 'Back to today',
                              ok: 'Ok',
                              clear: 'Clear',
                              month: 'Month',
                              year: 'Year',
                              timeSelect: 'Select time',
                              dateSelect: 'Select date',
                              monthSelect: 'Choose a month',
                              yearSelect: 'Choose a year',
                              decadeSelect: 'Choose a decade',
                              yearFormat: 'YYYY',
                              dateFormat: 'M/D/YYYY',
                              dayFormat: 'D',
                              dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                              monthFormat: 'MMMM',
                              monthBeforeYear: true,
                              previousMonth: 'Previous month (PageUp)',
                              nextMonth: 'Next month (PageDown)',
                              previousYear: 'Last year (Control + left)',
                              nextYear: 'Next year (Control + right)',
                              previousDecade: 'Last decade',
                              nextDecade: 'Next decade',
                              previousCentury: 'Last century',
                              nextCentury: 'Next century',
                            },
                            timePickerLocale: {
                              placeholder: 'Select time',
                            },
                            dateFormat: 'YYYY-MM-DD',
                            dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                            weekFormat: 'YYYY-wo',
                            monthFormat: 'YYYY-MM',
                          }}
                        />
                      </Form.Item>
                      <span className="standardBoldFont mg-10">〜</span>
                      <Form.Item name="lease_end_date">
                        <DatePicker
                          onChange={onChangeEndDate}
                          disabledDate={validateEndDate}
                          defaultValue={moment(itemDetail?.lease_end_date)}
                          name="lease_end_date"
                          className="input-date standardFont"
                          size={'large'}
                          locale={{
                            lang: {
                              locale: 'ja',
                              placeholder: 'Select date',
                              rangePlaceholder: ['Start date', 'End date'],
                              today: 'Today1',
                              now: 'Now',
                              backToToday: 'Back to today',
                              ok: 'Ok',
                              clear: 'Clear',
                              month: 'Month',
                              year: 'Year',
                              timeSelect: 'Select time',
                              dateSelect: 'Select date',
                              monthSelect: 'Choose a month',
                              yearSelect: 'Choose a year',
                              decadeSelect: 'Choose a decade',
                              yearFormat: 'YYYY',
                              dateFormat: 'M/D/YYYY',
                              dayFormat: 'D',
                              dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                              monthFormat: 'MMMM',
                              monthBeforeYear: true,
                              previousMonth: 'Previous month (PageUp)',
                              nextMonth: 'Next month (PageDown)',
                              previousYear: 'Last year (Control + left)',
                              nextYear: 'Next year (Control + right)',
                              previousDecade: 'Last decade',
                              nextDecade: 'Next decade',
                              previousCentury: 'Last century',
                              nextCentury: 'Next century',
                            },
                            timePickerLocale: {
                              placeholder: 'Select time',
                            },
                            dateFormat: 'YYYY-MM-DD',
                            dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                            weekFormat: 'YYYY-wo',
                            monthFormat: 'YYYY-MM',
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row className="row-statistics mb-39">
                    <Col className="standardBoldFont" span={5}>
                      現在のリース期間
                    </Col>
                    <Col span={19}>
                      <span className="standardFont">{moment(itemDetail?.lease_start_date).format(formatDate)}</span>
                      <span className="standardBoldFont mg-10">〜</span>
                      <span className="standardFont">{moment(itemDetail?.lease_end_date).format(formatDate)}</span>
                    </Col>
                  </Row>
                  <Row className="row-statistics mb-39">
                    <Col className="standardBoldFont" span={5}>
                      契約日
                    </Col>
                    <Col span={19}>
                      <span className="standardFont">{moment(itemDetail?.lease_contract_date).format(formatDate)}</span>
                    </Col>
                  </Row>
                  <Row className="row-statistics mb-39">
                    <Col className="standardBoldFont" span={5}>
                      台数
                    </Col>
                    <Col span={19}>
                      <span className="standardFont">{itemDetail?.machine_quantity}台</span>
                    </Col>
                  </Row>
                  <Row className="row-statistics mb-39">
                    <Col className="standardBoldFont" span={5}>
                      リース会社
                    </Col>
                    <Col span={19}>
                      <span className="standardFont">{itemDetail?.lease_company}</span>
                    </Col>
                  </Row>
                  <Row className="row-statistics mb-39">
                    <Col className="standardBoldFont" span={5}>
                      販売店
                    </Col>
                    <Col span={19}>
                      <span className="standardFont">
                        {itemDetail?.lease_shop}
                        {itemDetail?.lease_shop_other}
                      </span>
                    </Col>
                  </Row>
                  <Row className="row-statistics mb-10">
                    <Col className="standardBoldFont" span={5}>
                      契約番号
                    </Col>
                    <Col span={19}>
                      <span className="standardFont">{itemDetail?.lease_contract_id}</span>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="table-update-lease">
                <Rectangle text="リース機器一覧" />
                <TableUpdate
                  itemTable={itemDetail?.machines || []}
                  deviceChoose={devicesReturn}
                  onChangeDevice={changeDeviceReturn}
                />
              </div>
              <div className="confirm-update-lease">
                <Button
                  className="btn-cancel extraLargeFont"
                  onClick={() => history.push('/machine/detail', { itemId: itemDetail?.i_id })}
                >
                  キャンセル
                </Button>
                <SubmitButton className="btn-renew-the-lease extraLargeFont">リース期間を更新する</SubmitButton>
              </div>
            </Form>
          )}
        />
      </div>
      <ModalWarningLeavePage />
    </Wrap>
  );
};
export default MachineUpdate;
